// src/utils/importNormalize.js

/* ============== Helpers base ============== */
export const n = (v) => {
  if (v === "" || v == null) return null;
  const num = Number(v);
  return Number.isFinite(num) ? num : null; // evita NaN
};

export const brToBool = (v) => {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "sim", "ativo", "true", "yes"].includes(s)) return 1;
    if (["0", "não", "nao", "inativo", "false", "no"].includes(s)) return 0;
  }
  return Number(v) === 1 ? 1 : 0;
};

const strip = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** separa lista genérica (vírgula, ;, |, /, quebra de linha, tab…) */
const splitList = (v) => {
  if (v == null) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s
    .replace(/\r?\n/g, ",")
    .split(/[,|;/]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
};

/** imagens: aceita , ; | / e quebra de linha */
export const parseImageUrls = (v) => splitList(v);

/** Itens inclusos: ponto e vírgula ou quebra de linha. Não parte no meio de nome + SKU. */
export const splitIncludedItems = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) {
    return v.map((x) => String(x || "").trim()).filter(Boolean);
  }
  const s = String(v).trim();
  if (!s) return [];
  return s
    .replace(/\r?\n/g, ";")
    .split(";")
    .map((x) => x.trim())
    .filter(Boolean);
};

/* ============== Dicionário de sinônimos ============== */
/** Cada item vira um "alvo" interno. Mapeamos pelo cabeçalho já normalizado (strip). */
const SYNS = {
  // Campos essenciais
  nome: [
    "nome", "nome do produto", "produto", "titulo", "título",
    "descricao do produto", "descrição do produto", "nome produto",
    "nome comercial", "nome de venda"
  ],
  sku: [
    "sku", "codigo", "codigo do produto", "código", "código do produto", "cod produto", "id produto",
    "novo sku", "sku novo"
  ],

  // Preços
  preco: [
    "preco", "preço", "preco balcon sugerido", "preço balcão sugerido",
    "preco sugerido", "preço sugerido", "preco de tabela", "preço de tabela",
    "preco venda", "preço venda", "valor", "valor venda"
  ],
  preco_comdesconto: [
    "preco com desconto", "preço com desconto", "preco c desconto", "preço c desconto",
    "preco final", "preço final", "preco promocional", "preço promocional",
    "preco_comdesconto"
  ],
  estoque: [
    "estoque", "qtd", "qtde", "quantidade", "quantidade em estoque", "saldo", "saldo estoque"
  ],
  status: ["status", "ativo", "disponivel", "disponível"],
  detalhes: [
    "detalhes", "descricao", "descrição", "descricao detalhada", "descricao longa", "descricao completa",
    "descricao by ia", "descrição by ia", "descricao ia", "descrição ia"
  ],

  // Categoria / Subcategoria — ATENÇÃO:
  // IDs apenas com rótulos curtos; os rótulos longos da planilha (que vêm com TEXTO)
  // foram mapeados para *NOME* de categoria/subcategoria.
  id_categoria: [
    "id categoria", "id da categoria", "categoria id", "id_categoria"
  ],
  id_subcategoria: [
    "id subcategoria", "id da subcategoria", "subcategoria id", "id_subcategoria"
  ],
  categoria_nome: [
    "categoria", "nome da categoria",
    "id da categoria (definido na aba categorias)"
  ],
  subcategoria_nome: [
    "subcategoria", "nome da subcategoria",
    "id da subcategoria (definido na aba subcategorias)"
  ],

  // Dimensões & peso
  peso: ["peso", "kg", "massa", "peso novo", "novo peso"],
  largura: ["largura", "width", "larg"],
  altura: ["altura", "height", "alt"],
  comprimento: ["comprimento", "profundidade", "length", "comp"],

  // Imagens
  imagens_urls: [
    "imagens_urls", "imagens", "urls_imagens", "urls das imagens", "urls_das_imagens",
    "url da imagem", "urls da imagem", "url imagem", "url imagens",
    "urls das imagens do produto separadas por virgula",
    "urls das imagens do produto separadas por virgula ()",
    "urls das imagens do produto separadas por virgula ,",
    "urls das imagens do produto separadas por virgula (,)",
    "urls das imagens do produto separadas por vírgula",
    "urls das imagens do produto separadas por vírgula (,)"
  ],

  // Aplicação (séries e motores)
  aplicacao_serie_truck: [
    "serie caminhao", "série caminhao", "série caminhão", "serie de caminhao",
    "serie caminho", "linha caminhao", "série caminho", "serie truck", "series truck"
  ],
  aplicacao_serie_bus: [
    "serie onibus", "série onibus", "serie onibus", "série ônibus", "serie de onibus",
    "linha onibus", "linha ônibus", "serie bus", "series bus"
  ],
  aplicacao_motores: [
    "motores", "motor", "linha de motores", "familia motor", "família motor",
    "codigo do motor", "codigos de motores", "códigos de motores"
  ],

  // Campo de descrição por IA explicitamente (para priorizar)
  descricao_ia: ["descricao by ia", "descrição by ia", "descricao ia", "descrição ia"],

  // Itens inclusos (texto livre do kit / conteúdo da embalagem)
  inclusos_itens: [
    "itens inclusos",
    "itens inclusos conteudo da embalagem",
    "inclusos",
    "conteudo da caixa",
    "conteudo da embalagem",
    "itens do kit",
    "conteudo do kit",
    "produtos inclusos",
    "itens que compoem o kit",
  ],
};

/** Inverte o dicionário para lookup rápido */
const LUT = (() => {
  const m = new Map();
  Object.entries(SYNS).forEach(([target, list]) => {
    list.forEach((alias) => m.set(strip(alias), target));
  });
  return m;
})();

const isBlank = (val) => val == null || (typeof val === "string" && val.trim() === "");

const CHOICE_FIELDS = [
  {
    field: "nome",
    hint: (k) => k.includes("nome") && !k.includes("categoria"),
  },
  {
    field: "sku",
    hint: (k) => k.includes("sku"),
  },
  {
    field: "preco",
    hint: (k) =>
      (k.includes("preco") || k.includes("valor")) &&
      !k.includes("desconto") &&
      !k.includes("promocional") &&
      !k.includes("final"),
  },
  {
    field: "detalhes",
    hint: (k) => k.includes("descricao") || k.includes("detalhes"),
  },
  {
    field: "peso",
    hint: (k) => k.includes("peso"),
  },
];

const cellFilled = (val) => !isBlank(val);

/** Remove cabeçalhos em que todas as células estão vazias. */
export const dropEmptyColumns = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const headers = Object.keys(rows[0] || {});
  const keep = headers.filter((h) => rows.some((r) => cellFilled(r?.[h])));
  return rows.map((r) => {
    const next = {};
    keep.forEach((h) => {
      next[h] = r?.[h];
    });
    return next;
  });
};

export const listFilledSourceFields = (rows = []) => {
  const fields = new Set();
  if (!Array.isArray(rows) || !rows.length) return fields;
  const headers = Object.keys(rows[0] || {});
  for (const h of headers) {
    if (!rows.some((r) => cellFilled(r?.[h]))) continue;
    const k = strip(h);
    const mapped = LUT.get(k);
    if (mapped) fields.add(mapped);
    for (const { field, hint } of CHOICE_FIELDS) {
      if (mapped && mapped !== field) continue;
      if (hint(k)) fields.add(field);
    }
  }
  return fields;
};

/**
 * Colunas da planilha que competem pelo mesmo campo (ex.: Nome do produto e NOME COMERCIAL).
 * Só entra na lista se pelo menos duas colunas tiverem dado preenchido.
 */
export const findAmbiguousFields = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const headers = Object.keys(rows[0] || {});
  const filledCount = (header) =>
    rows.reduce((n, r) => n + (cellFilled(r?.[header]) ? 1 : 0), 0);
  const sampleOf = (header) => {
    const hit = rows.find((r) => cellFilled(r?.[header]));
    if (!hit) return "";
    return String(hit[header]).replace(/\s+/g, " ").trim().slice(0, 80);
  };

  const out = [];
  for (const { field, hint } of CHOICE_FIELDS) {
    const columns = headers.filter((h) => {
      const k = strip(h);
      const mapped = LUT.get(k);
      if (mapped === field) return true;
      if (mapped && mapped !== field) return false;
      return hint(k);
    });
    const withData = columns.filter((h) => filledCount(h) > 0);
    if (withData.length < 2) continue;
    out.push({
      field,
      columns: withData.map((header) => ({
        header,
        sample: sampleOf(header),
        filled: filledCount(header),
      })),
    });
  }
  return out;
};

export const defaultColumnChoices = (ambiguous = []) => {
  const choices = {};
  for (const group of ambiguous) {
    const lutHit = group.columns.find((c) => LUT.get(strip(c.header)) === group.field);
    choices[group.field] = (lutHit || group.columns[0]).header;
  }
  return choices;
};

/**
 * Dado um objeto de uma linha do XLSX (chaves = cabeçalhos originais),
 * cria um "row normalizado" com base em sinônimos + regras.
 */
export const normalizeRow = (row, columnChoices = {}) => {
  // 1) cria um índice strip(key) -> valor
  const idx = new Map();
  Object.entries(row || {}).forEach(([k, v]) => idx.set(strip(k), v));

  const valueFromHeader = (header) => {
    if (!header) return undefined;
    if (Object.prototype.hasOwnProperty.call(row || {}, header)) return row[header];
    const hit = Object.keys(row || {}).find((k) => strip(k) === strip(header));
    return hit ? row[hit] : undefined;
  };

  // 2) busca utilitária por alvo (campo interno) via LUT.
  // Célula vazia não ganha da próxima coluna com o mesmo significado
  // (ex.: Detalhes em branco não apaga Descrição preenchida).
  const get = (target) => {
    const chosen = columnChoices?.[target];
    if (chosen) return valueFromHeader(chosen);
    let emptyHit;
    for (const [kNorm, val] of idx.entries()) {
      const t = LUT.get(kNorm);
      if (t !== target) continue;
      if (!isBlank(val)) return val;
      if (emptyHit === undefined) emptyHit = val;
    }
    return emptyHit;
  };

  // 3) monta objeto final, aplicando coerções e defaults
  const nome = get("nome") ?? row.nome ?? row.NOME ?? row.Nome ?? "";
  const sku =
    get("sku") ??
    row.sku ??
    row.SKU ??
    row.Sku ??
    null;

  // preços
  const preco = n(get("preco") ?? row.preco ?? row.Preço ?? row.Preco);
  const precoComDesc = n(
    get("preco_comdesconto") ??
      row.preco_comdesconto ??
      row["preco c/ desconto"] ??
      row["preço c/ desconto"] ??
      row["preço final"]
  );

  const estoque = n(get("estoque") ?? row.estoque ?? row.Estoque);
  const status = brToBool(get("status") ?? row.status ?? row.Status ?? 1);

  // descrição: prioriza "Descrição by IA" se vier, salvo escolha explícita do usuário
  const descIA = columnChoices?.detalhes ? null : get("descricao_ia");
  const detalhesRaw =
    get("detalhes") ??
    row.detalhes ??
    row.Detalhes ??
    row["Descrição"] ??
    row["Descricao"];
  const detalhes = String(descIA || detalhesRaw || "").trim();

  // Categoria/Subcategoria
  // IDs: apenas se realmente numéricos
  const rawIdCat = get("id_categoria") ?? row.id_categoria ?? row.categoria_id ?? null;
  const rawIdSub = get("id_subcategoria") ?? row.id_subcategoria ?? row.subcategoria_id ?? null;
  const id_categoria = n(rawIdCat);
  const id_subcategoria = n(rawIdSub);

  // Nomes: incluem os rótulos longos que vêm com texto
  const categoria_nome =
    get("categoria_nome") ??
    row.categoria ??
    row.Categoria ??
    null;

  const subcategoria_nome =
    get("subcategoria_nome") ??
    row.subcategoria ??
    row.Subcategoria ??
    null;

  // dimensões
  const peso = n(get("peso") ?? row.peso ?? row.Peso);
  const largura = n(get("largura") ?? row.largura ?? row.Largura);
  const altura = n(get("altura") ?? row.altura ?? row.Altura);
  const comprimento = n(get("comprimento") ?? row.comprimento ?? row.Comprimento);

  // imagens (concatena se houver múltiplos campos reconhecidos)
  const imgsPrim =
    get("imagens_urls") ??
    row.imagens_urls ??
    row.imagens ??
    row["urls_imagens"] ??
    row["urls_das_imagens"];
  const imagens_urls = (Array.isArray(imgsPrim) ? imgsPrim.join(",") : String(imgsPrim || "")).trim();

  // aplicação: séries caminhão/ônibus & motores
  const serieTruck = splitList(get("aplicacao_serie_truck"));
  const serieBus = splitList(get("aplicacao_serie_bus"));
  const motores = splitList(get("aplicacao_motores"));

  const inclusos_itens = splitIncludedItems(
    get("inclusos_itens") ?? row.inclusos_itens ?? row.inclusos ?? row["Itens inclusos"]
  );

  return {
    id: row.id ?? row.ID ?? row.Id ?? undefined,
    sku,
    nome,
    tipo: n(row.tipo ?? row.Tipo) || 1,

    preco,
    preco_comdesconto: precoComDesc,
    estoque,
    status,
    detalhes,

    id_categoria,
    id_subcategoria,
    categoria_nome,
    subcategoria_nome,

    peso,
    largura,
    altura,
    comprimento,

    imagens_urls,

    // Extras para enriquecimento no apply
    aplicacao_serie_truck: serieTruck,
    aplicacao_serie_bus: serieBus,
    aplicacao_motores: motores,
    inclusos_itens,
    descricao_ia: descIA ? String(descIA).trim() : null,
  };
};
